import { useSyncExternalStore } from 'react';

import type { ChatHistoryByOrg } from '@cast/types';

export const OPS_PILOT_CHAT_HISTORY_KEY = 'ai-assistant-chat-history';

const listeners = new Set<() => void>();

const subscribeToOpsPilotChatHistory = (
  onStoreChange: () => void
): (() => void) => {
  listeners.add(onStoreChange);
  return () => {
    listeners.delete(onStoreChange);
  };
};

/** Notify onboarding (and other same-tab consumers) after chat history writes. */
export const notifyOpsPilotChatHistoryChanged = (): void => {
  for (const listener of listeners) {
    listener();
  }
};

const readChatHistoryByOrg = (): ChatHistoryByOrg => {
  try {
    const raw = window.localStorage.getItem(OPS_PILOT_CHAT_HISTORY_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    return parsed as ChatHistoryByOrg;
  } catch {
    return {};
  }
};

let cachedRaw: string | null = null;
let cachedHistory: ChatHistoryByOrg = {};

/**
 * Parsed history memoized on the raw localStorage string. `useSyncExternalStore`
 * calls its snapshot on every render and consistency check, so re-parsing the
 * full payload each time is wasteful while the value is unchanged.
 */
const readChatHistoryByOrgCached = (): ChatHistoryByOrg => {
  const raw = window.localStorage.getItem(OPS_PILOT_CHAT_HISTORY_KEY);
  if (raw === cachedRaw) {
    return cachedHistory;
  }
  cachedHistory = readChatHistoryByOrg();
  cachedRaw = raw;
  return cachedHistory;
};

/**
 * True when this browser has any OpsPilot chat with a user message for the org.
 * Bridges PostHog readiness lag (API may stay false for ~15 minutes).
 */
export const hasOpsPilotQuestionsInLocalStorage = (
  organizationId: string | undefined
): boolean => {
  if (!organizationId || typeof window === 'undefined') {
    return false;
  }

  const orgHistory = readChatHistoryByOrgCached()[organizationId];
  if (!Array.isArray(orgHistory) || orgHistory.length === 0) {
    return false;
  }

  return orgHistory.some(
    (chat) =>
      Array.isArray(chat?.messages) &&
      chat.messages.some((message) => message?.role === 'user')
  );
};

export const useHasOpsPilotQuestionsInLocalStorage = (
  organizationId: string | undefined
): boolean =>
  useSyncExternalStore(
    subscribeToOpsPilotChatHistory,
    () => hasOpsPilotQuestionsInLocalStorage(organizationId),
    () => false
  );
